import Plyr from 'plyr';
import Flickity from 'flickity';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';
import { sleep } from './lib/utils.js';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

class FF_SunScroll {
	scroller: ReturnType<typeof ScrollSmoother.create>;

	constructor() {
		if (window.innerWidth <= 540) return;
		this.smooth();
		this.statsTransitions();
		this.diagramTransitions();
		this.slideFades();
		this.slideFadeSections();
		this.refresh();
	}

	async refresh() {
		await sleep(500);
		ScrollTrigger.refresh();
	}
	smooth() {
		this.scroller = ScrollSmoother.create({
			normalizeScroll: true,
			smooth: 1,
			effects: true,
		});
	}

	statsTransitions() {
		const trigger = document.querySelector('.sun-stats');
		const elements = trigger.querySelectorAll('.stat');
		const rules = trigger.querySelectorAll('.rule');
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: trigger,
				toggleActions: 'play none none reverse',
				start: 'top 80%', // when the top of the trigger hits the top of the viewport
			},
		});
		rules.forEach((el, i) => {
			tl.from(el, { autoAlpha: 0, duration: 1, delay: (i + 1) * 0.4 }, 'start');
		});
		elements.forEach((el, i) => {
			const label = el.querySelector('.label');
			const value = el.querySelector('.value');
			tl.from(label, { x: -20, autoAlpha: 0, duration: 0.7, delay: i * 0.4 }, 'start');
			tl.from(value, { x: -20, autoAlpha: 0, duration: 0.7, delay: i * 0.4 + 0.2 }, 'start');
		});
	}

	diagramTransitions() {
		const layers = document.querySelectorAll('.js-sunDiagramLayer');
		const labels = [...document.querySelectorAll('.js-sunDiagramLayer .measureWrapper')].reverse();
		layers.forEach((el) => {
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: el,
					toggleActions: 'play none none reverse',
					start: 'top 80%', // when the top of the trigger hits the top of the viewport
				},
			});
			tl.from(el, { autoAlpha: 0, duration: 1 });
		});

		const labelTl = gsap.timeline({
			scrollTrigger: {
				trigger: layers[2],
				toggleActions: 'play none none reverse',
				start: 'top 80%', // when the top of the trigger hits the top of the viewport
			},
		});
		labels.forEach((el, i) => {
			const text = el.querySelector('.measure');
			const rule = el.querySelector('.candleRule');
			labelTl.from(rule, { autoAlpha: 0, duration: 1, delay: i * 0.4 }, 'start');
			labelTl.from(text, { autoAlpha: 0, x: -20, duration: 1, delay: i * 0.4 + 0.2 }, 'start');
		});
	}

	slideFades() {
		const els = document.querySelectorAll('.js-scrollSlideFade');
		els.forEach((el) => {
			const delay = Number((el as HTMLElement).dataset.delay) || 0;
			gsap.from(el, {
				scrollTrigger: {
					trigger: el,
					toggleActions: 'play none none reverse',
					start: 'top 80%',
				},
				x: -20,
				autoAlpha: 0,
				delay,
			});
		});
	}

	slideFadeSections() {
		const sections = document.querySelectorAll('.js-scrollSlideFadeSecion');
		sections.forEach((section) => {
			const trigger = section.querySelector('.js-scrollSlideFadeSecionTrigger') || section;
			const els = section.querySelectorAll('.js-scrollSlideFadeSecionEl');
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger,
					toggleActions: 'play none none reverse',
					start: 'top 80%',
				},
			});
			els.forEach((el, i) => {
				tl.from(el, { x: -20, autoAlpha: 0, duration: 1, delay: i * 0.4 }, 'start');
			});
		});
	}
}
new FF_SunScroll();

class FF_SunGallery extends HTMLElement {
	hovering = false;
	index = 0;
	hoverIndex = 0;
	players: GalleryPlayerEntry[] = [];
	playingVideo = 0;
	data: GallerySlideData[] = [];
	DOM = {} as Record<string, any>;
	flkty: FlickityInstance;

	constructor() {
		super();
		this.data = JSON.parse(galleryData);
		this.cache();
		this.events();
		this.initVideos();
		this.initRotator();
	}

	cache() {
		this.DOM = {
			next: this.querySelector('.js-next'),
			prev: this.querySelector('.js-prev'),
			controlPanel: this.querySelector('.js-controlPanel'),
			dots: this.querySelectorAll('.js-dot'),
			hoverTitle: this.querySelector('.js-hoverTitle'),
			rotator: this.querySelector('.js-rotator'),
			slides: this.querySelectorAll('.js-slide'),
		};
	}

	events() {
		this.DOM.prev.addEventListener('click', this.prevClick.bind(this));
		this.DOM.next.addEventListener('click', this.nextClick.bind(this));
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].addEventListener('click', this.dotClick.bind(this, i));
			this.DOM.dots[i].addEventListener('mouseenter', this.dotEnter.bind(this, i));
		}
		this.DOM.controlPanel.addEventListener('mouseenter', this.controlEnter.bind(this));
		this.DOM.controlPanel.addEventListener('mouseleave', this.controlLeave.bind(this));
	}

	initVideos() {
		for (let i = 0; i < this.DOM.slides.length; i++) {
			const el = this.DOM.slides[i].querySelector('.js-video');
			let plyr;
			if (el) {
				plyr = new Plyr(el, {
					controls: ['play'],
					loop: { active: true },
					iconUrl: `${templateDirectoryUri}/partials/ember/plyr.svg`,
				});
				if (i === 0) {
					plyr.muted = true; //this is weirdly needed
				} else {
					plyr.muted = false;
				}
			}
			this.players.push({
				plyr: plyr,
			});
		}
	}

	initRotator() {
		this.flkty = new Flickity(this.DOM.rotator, {
			pageDots: false,
			prevNextButtons: false,
		}) as unknown as FlickityInstance;
		this.flkty.on('change', this.rotatorChange.bind(this));
	}

	prevClick() {
		this.flkty.previous();
	}
	nextClick() {
		this.flkty.next();
	}
	dotClick(i: number) {
		this.flkty.select(i);
	}
	dotEnter(index: number) {
		this.hoverIndex = index;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		this.DOM.dots[index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[index].title;
	}
	controlEnter() {
		this.hovering = true;
	}
	controlLeave() {
		this.hovering = false;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		this.DOM.dots[this.index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[this.index].title;
	}
	rotatorChange(index: number) {
		this.index = index;
		for (let i = 0; i < this.DOM.dots.length; i++) {
			this.DOM.dots[i].classList.remove('js-active');
		}
		if (index === this.DOM.dots.length - 1) {
			this.DOM.next.disabled = true;
		} else {
			this.DOM.next.disabled = false;
		}
		if (index === 0) {
			this.DOM.prev.disabled = true;
		} else {
			this.DOM.prev.disabled = false;
		}
		if (this.players[this.playingVideo].plyr) {
			this.players[this.playingVideo].plyr.pause();
		}
		if (this.players[index].plyr) {
			void Promise.resolve(this.players[index].plyr.play()).catch(() => {});
		}
		this.playingVideo = index;

		if (this.hovering) return;
		this.DOM.dots[index].classList.add('js-active');
		this.DOM.hoverTitle.innerHTML = this.data[index].title;
	}
}
customElements.define('sun-gallery', FF_SunGallery);
